import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  type ListContactDto,
  type ListSigningDto,
  type ListTemplateDto,
  type OrganizationMember,
} from 'xosign-types';

type SortFunc<T> = (a: T, b: T, sortBy: keyof T, dir: number) => number;

interface ListingFunctionsProps<T extends object> {
  baseRoute?: string;
  defaultSortBy?: string;
  fixedSortDir?: 'asc' | 'desc' | null;
  onClose?: (() => Promise<unknown>) | (() => void);
  searchKeys?: (keyof T)[];
  searchTerm?: string;
  sortFn?: Partial<Record<keyof T, SortFunc<T>>>;
}

const useListingFunctions = <
  T extends
    | ListContactDto
    | ListSigningDto
    | ListTemplateDto
    | OrganizationMember,
>({
  baseRoute,
  defaultSortBy = 'name',
  fixedSortDir = null,
  onClose,
  sortFn,
  searchKeys,
  searchTerm,
}: ListingFunctionsProps<T>) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [deletingItems, setDeletingItems] = useState<string[]>([]);

  const defaultSort = (itemA: T, itemB: T, sortBy: keyof T, dir: number) => {
    if (
      typeof itemA[sortBy] === 'string' &&
      typeof itemB[sortBy] === 'string'
    ) {
      return itemA[sortBy].localeCompare(itemB[sortBy]) * dir;
    }
    if (itemA[sortBy] > itemB[sortBy]) {
      return dir;
    }
    if (itemA[sortBy] < itemB[sortBy]) {
      return -1 * dir;
    }
    return 0;
  };

  const handleChecked = (id: string) => {
    if (checkedItems.includes(id)) {
      setCheckedItems((prevState) => {
        return prevState.filter((item) => item !== id);
      });
    } else {
      setCheckedItems((prevState) => {
        return [...prevState, id];
      });
    }
  };

  const handleUncheck = () => {
    setCheckedItems([]);
  };

  const validateChecked = (entriesToProcess: T[]) => {
    setCheckedItems((prevState) => {
      return prevState.filter((elementId) => {
        return entriesToProcess.some((s) => {
          return s.id === elementId;
        });
      });
    });
  };

  const handleDetailClose = () => {
    if (baseRoute) {
      void navigate(baseRoute);
    }

    void onClose?.();
  };

  const searchAndSort = (entriesToProcess: T[]) => {
    const sortBy = (searchParams.get('sortBy') ?? defaultSortBy) as keyof T;
    let dir = searchParams.get('dir') === 'desc' ? -1 : 1;

    if (fixedSortDir) {
      dir = fixedSortDir === 'desc' ? -1 : 1;
    }

    let filtered = [...entriesToProcess];

    if (searchTerm && searchTerm.length > 0) {
      filtered = searchKeys
        ? entriesToProcess.filter((item) => {
            return searchKeys.some((searchKey) => {
              return String(item[searchKey])
                .toLowerCase()
                .includes(searchTerm.toLowerCase());
            });
          })
        : entriesToProcess;
    }

    return filtered.toSorted((itemA, itemB) => {
      if (sortFn?.[sortBy]) {
        return sortFn[sortBy](itemA, itemB, sortBy, dir);
      }

      return defaultSort(itemA, itemB, sortBy, dir);
    });
  };

  return {
    checkedItems,
    deletingItems,
    handleChecked,
    handleDetailClose,
    handleUncheck,
    searchAndSort,
    setDeletingItems,
    validateChecked,
  };
};

export default useListingFunctions;
